import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  CardHeader,
  CardContent,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  Button,
  Divider,
} from '@mui/material';
import GlassCard from '../Cards/GlassCard';
import { panelMutedSurfaceSx } from '../../utils/colors';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';

import LeetcodeHeatmap from './LeetcodeHeatmap';
import useLeetcodeHeatmap from '../../hooks/useLeetcodeHeatmap';

// ── Date helpers ──────────────────────────────────────────────────────────────

/** Returns today as 'YYYY-MM-DD' in local time. */
const today = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

/**
 * Returns the date N days before today as 'YYYY-MM-DD'.
 * Safe for days=0 (returns today).
 */
const daysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

/** Returns the date N months before today as 'YYYY-MM-DD'. */
const monthsAgo = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
};

// ── Preset definitions ────────────────────────────────────────────────────────

const PRESETS = [
  {
    key:   '30d',
    label: 'Last 30 days',
    getRange: () => ({ startDate: daysAgo(29),    endDate: today() }),
  },
  {
    key:   '90d',
    label: 'Last 90 days',
    getRange: () => ({ startDate: daysAgo(89),    endDate: today() }),
  },
  {
    key:   '6mo',
    label: 'Last 6 months',
    getRange: () => ({ startDate: monthsAgo(6),   endDate: today() }),
  },
  {
    key:   'custom',
    label: 'Custom range',
    getRange: null, // handled by user-supplied date inputs
  },
];

const DEFAULT_PRESET = '90d';

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * LeetcodeAnalyticsPanel
 *
 * A self-contained panel that combines:
 *   • Preset date-range buttons (Last 30 days / 90 days / 6 months / Custom)
 *   • A custom date-range form (shown only when "Custom range" is selected)
 *   • The LeetcodeHeatmap visualisation
 *   • Loading / error states
 *
 * The heatmap is automatically refetched whenever the active date range
 * changes (preset switches OR the custom date inputs are applied).
 *
 * @param {string}      userId          - User._id or StudentProfile._id
 * @param {string}      [title]         - Card header title (default: "Submission Activity")
 * @param {string}      [subheader]     - Optional subtitle below the title
 */
const LeetcodeAnalyticsPanel = ({ userId, title = 'Submission Activity', subheader }) => {
  // ── Active preset ─────────────────────────────────────────────────────────
  const [activePreset, setActivePreset] = useState(DEFAULT_PRESET);

  // ── Custom date-range input state ─────────────────────────────────────────
  const defaultCustomRange  = PRESETS.find((p) => p.key === DEFAULT_PRESET).getRange();
  const [customStart, setCustomStart] = useState(defaultCustomRange.startDate);
  const [customEnd,   setCustomEnd]   = useState(defaultCustomRange.endDate);

  // The "applied" custom range — only updated when the user clicks Apply
  const [appliedCustomStart, setAppliedCustomStart] = useState(null);
  const [appliedCustomEnd,   setAppliedCustomEnd]   = useState(null);

  // ── Derived active date range ─────────────────────────────────────────────
  const { startDate, endDate } = useMemo(() => {
    if (activePreset === 'custom') {
      return {
        startDate: appliedCustomStart ?? customStart,
        endDate:   appliedCustomEnd   ?? customEnd,
      };
    }
    const preset = PRESETS.find((p) => p.key === activePreset);
    return preset.getRange();
  }, [activePreset, appliedCustomStart, appliedCustomEnd, customStart, customEnd]);

  // ── Heatmap data ──────────────────────────────────────────────────────────
  const { heatmapData, loading, error, errorType, refetch } =
    useLeetcodeHeatmap(userId, startDate, endDate);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePresetChange = useCallback((_e, value) => {
    if (!value) return; // don't deselect
    setActivePreset(value);
    // Reset applied custom range so switching back to custom shows fresh inputs
    if (value !== 'custom') {
      setAppliedCustomStart(null);
      setAppliedCustomEnd(null);
    }
  }, []);

  const handleApplyCustom = useCallback(() => {
    if (!customStart || !customEnd) return;
    setAppliedCustomStart(customStart);
    setAppliedCustomEnd(customEnd);
  }, [customStart, customEnd]);

  // ── Validation for custom range inputs ───────────────────────────────────
  const customRangeError = useMemo(() => {
    if (activePreset !== 'custom' || !customStart || !customEnd) return null;
    const sMs = Date.parse(customStart);
    const eMs = Date.parse(customEnd);
    if (sMs > eMs) return 'Start date must be on or before end date.';
    if ((eMs - sMs) / 86_400_000 > 365) return 'Range must not exceed 365 days.';
    return null;
  }, [activePreset, customStart, customEnd]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <GlassCard sx={{ borderRadius: 4 }}>
      <CardHeader
        avatar={<CalendarMonthRoundedIcon sx={{ color: 'primary.main' }} />}
        title={
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
        }
        subheader={
          subheader && (
            <Typography variant="body2" color="text.secondary">
              {subheader}
            </Typography>
          )
        }
        action={
          <Button
            size="small"
            startIcon={<RefreshRoundedIcon />}
            onClick={refetch}
            disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 600, mr: 1 }}
          >
            Refresh
          </Button>
        }
        sx={{ px: 2.5, pt: 2.5, pb: 0 }}
      />

      <CardContent sx={{ px: 2.5, pt: 2, pb: 2.5, '&:last-child': { pb: 2.5 } }}>

        {/* ── Date range controls ───────────────────────────────────────── */}
        <Box
          sx={{
            display:    'flex',
            alignItems: 'flex-start',
            flexWrap:   'wrap',
            gap:        1.5,
            mb:         2,
          }}
        >
          <ToggleButtonGroup
            value={activePreset}
            exclusive
            onChange={handlePresetChange}
            size="small"
            sx={{
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                px:            1.5,
                py:            0.4,
                fontSize:      '0.78rem',
                textTransform: 'none',
                fontWeight:    600,
                borderRadius:  '8px !important',
                border:        '1px solid',
                borderColor:   'divider',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color:   '#fff',
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              },
            }}
          >
            {PRESETS.map(({ key, label }) => (
              <ToggleButton key={key} value={key}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* ── Custom date-range inputs ──────────────────────────────────── */}
        <Collapse in={activePreset === 'custom'}>
          <Box
            sx={{
              display:     'flex',
              flexWrap:    'wrap',
              gap:         1.5,
              alignItems:  'flex-start',
              mb:          2,
              p:           2,
              borderRadius: 2,
              ...panelMutedSurfaceSx,
              border:      '1px solid',
              borderColor: 'divider',
            }}
          >
            <TextField
              label="Start date"
              type="date"
              size="small"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              inputProps={{ max: customEnd || today() }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="End date"
              type="date"
              size="small"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              inputProps={{ min: customStart, max: today() }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyCustom}
              disabled={!!customRangeError || !customStart || !customEnd}
              sx={{ textTransform: 'none', fontWeight: 600, alignSelf: 'center' }}
            >
              Apply
            </Button>
            {customRangeError && (
              <Typography variant="caption" color="error" sx={{ alignSelf: 'center' }}>
                {customRangeError}
              </Typography>
            )}
          </Box>
        </Collapse>

        <Divider sx={{ mb: 2 }} />

        {/* ── Loading ───────────────────────────────────────────────────── */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} />
          </Box>
        )}

        {/* ── Error ─────────────────────────────────────────────────────── */}
        {!loading && error && (
          <Alert
            severity={errorType === 'notFound' ? 'warning' : 'error'}
            sx={{ borderRadius: 2 }}
            action={
              <Button size="small" onClick={refetch} sx={{ textTransform: 'none' }}>
                Retry
              </Button>
            }
          >
            {errorType === 'validation' && 'Invalid date range: '}
            {errorType === 'notFound'   && 'Student not found: '}
            {error}
          </Alert>
        )}

        {/* ── Heatmap ───────────────────────────────────────────────────── */}
        {!loading && !error && heatmapData && (
          <LeetcodeHeatmap
            heatmapData={heatmapData}
            startDate={startDate}
            endDate={endDate}
          />
        )}

        {/* ── No-data empty state ───────────────────────────────────────── */}
        {!loading && !error && !heatmapData && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No submission data available.
            </Typography>
          </Box>
        )}

      </CardContent>
    </GlassCard>
  );
};

export default LeetcodeAnalyticsPanel;
